import type { FulfillmentOrderLineItem } from "./fulfillment-order-line-item";

/**
 * The delivery method name used to identify consolidation shipments.
 * Set when creating the consolidation FG workflow and matched when
 * determining whether a fulfillment group is a consolidation type.
 */
export const CONSOLIDATION_DELIVERY_METHOD_NAME = "Consolidation Shipment";

export function isConsolidationFulfillmentGroup(fg: {
  fulfillmentOrders?: { deliveryMethod?: { name?: string | null } }[];
}): boolean {
  return (
    fg.fulfillmentOrders?.some(
      (order) =>
        order.deliveryMethod?.name === CONSOLIDATION_DELIVERY_METHOD_NAME,
    ) ?? false
  );
}

export function consolidationCycleKey(cycleDate: Date): string {
  return cycleDate.toISOString().slice(0, 10);
}

export function consolidationTransferWorkflowId(args: {
  fulfillmentServiceTeamId: string;
  merchantTeamId: string;
  originLocationId: string;
  cycleKey: string;
}): string {
  return `consolidation-fg-${args.fulfillmentServiceTeamId}-${args.merchantTeamId}-${args.originLocationId}-${args.cycleKey}`;
}

export function consolidationTransferIdempotencyKey(
  workflowId: string,
): string {
  return `inventory-item-transfer-${workflowId}`;
}

export const ConsolidationPackingSlipGroupKeyKind = {
  redoProductId: "redoProductId",
  sku: "sku",
} as const;
export type ConsolidationPackingSlipGroupKeyKind =
  (typeof ConsolidationPackingSlipGroupKeyKind)[keyof typeof ConsolidationPackingSlipGroupKeyKind];

/**
 * Roll per-inventory-item consolidation line items up to one row per product
 * for packing-slip display. A consolidation FG creates one line item per
 * distinct `imsInventoryItemId` (quantity 1) so that pick-wave bin resolution
 * can target individual license plates — this is load-bearing and must not be
 * changed on the FG itself. On a packing slip a merchant only cares about
 * "how many of each SKU arrived", so we group here for display.
 *
 * Grouping key prefers `redoProductId`; falls back to `sku` when the product
 * id is absent. Items with neither are preserved as their own row so we never
 * accidentally merge unrelated line items.
 */
export function groupConsolidationLineItemsForPackingSlip(
  items: readonly FulfillmentOrderLineItem[],
): FulfillmentOrderLineItem[] {
  const grouped = new Map<string, FulfillmentOrderLineItem>();
  const ungrouped: FulfillmentOrderLineItem[] = [];

  for (const item of items) {
    const key = consolidationPackingSlipGroupKey(item);
    if (key === null) {
      ungrouped.push(item);
      continue;
    }
    const existing = grouped.get(key);
    if (!existing) {
      grouped.set(key, { ...item });
    } else {
      existing.totalQuantity += item.totalQuantity;
      existing.unfulfilledQuantity += item.unfulfilledQuantity;
      existing.quantity += item.quantity;
    }
  }

  return [...grouped.values(), ...ungrouped];
}

function consolidationPackingSlipGroupKey(
  item: FulfillmentOrderLineItem,
): string | null {
  if (item.redoProductId) {
    return `${ConsolidationPackingSlipGroupKeyKind.redoProductId}:${item.redoProductId}`;
  }
  if (item.sku) {
    return `${ConsolidationPackingSlipGroupKeyKind.sku}:${item.sku}`;
  }
  return null;
}
